'use client';

import { cn } from '../../../../lib/utils';
import {
  PauseFill,
  PlayArrowFill,
  VolumeOffFill,
  VolumeUpFill,
} from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';
import { useState } from 'react';

export interface SwitchButtonProps extends React.ComponentProps<'div'> {
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /** 컴포넌트 상태 */
  isOn?: boolean;
  /** 컴포넌트 상태 변경 이벤트 */
  onIsOnChange?: (isOn: boolean) => void;
  /** 타입 */
  type: 'play/pause' | 'on/off';
  /** 켜질 때 텍스트 */
  onText?: string | React.ReactNode;
  /** 꺼질 때 텍스트 */
  offText?: string | React.ReactNode;
}

const playPauseIconVariants = cva(
  ['shrink-0', '[&_path]:fill-comp-switch-button-text'],
  {
    variants: {
      size: {
        xsmall: 'size-[32px]',
        small: 'size-[48px]',
        medium: 'size-[64px]',
        large: 'size-[80px]',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
);

const switchButtonVariants = cva(
  [
    'flex items-center justify-center',
    'bg-comp-switch-button-bg cursor-pointer select-none',
    'rounded-full',
  ],
  {
    variants: {
      type: {
        'on/off': 'px-2xs py-5xs',
        'play/pause': 'p-2xs',
      },
    },
  },
);

function SwitchButton({
  size = 'medium',
  type = 'play/pause',
  isOn: externalIsOn,
  onIsOnChange: externalOnIsOnChange,
  onText,
  offText,
  className,
  ...props
}: SwitchButtonProps) {
  const [internalIsOn, setInternalIsOn] = useState(false);

  const isControlled = externalIsOn !== undefined;
  const isOn = isControlled ? externalIsOn : internalIsOn;

  const handleIsOnChange = () => {
    const newIsOn = !isOn;
    if (isControlled) {
      externalOnIsOnChange?.(newIsOn);
    } else {
      setInternalIsOn(newIsOn);
      externalOnIsOnChange?.(newIsOn);
    }
  };

  const getSwitchChildren = () => {
    switch (type) {
      case 'on/off':
        return (
          <div className="flex items-center">
            {isOn ? (
              <>
                <VolumeUpFill className="size-[32px] shrink-0 [&_path]:fill-comp-switch-button-text" />
                {onText && (
                  <div className="text-comp-accent-medium text-comp-switch-button-text">
                    {onText}
                  </div>
                )}
              </>
            ) : (
              <>
                <VolumeOffFill className="size-[32px] shrink-0 [&_path]:fill-comp-switch-button-text" />
                {offText && (
                  <div className="text-comp-accent-medium text-comp-switch-button-text">
                    {offText}
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 'play/pause':
        return isOn ? (
          <PauseFill className={playPauseIconVariants({ size })} />
        ) : (
          <PlayArrowFill className={playPauseIconVariants({ size })} />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn('group', switchButtonVariants({ type }), className)}
      onClick={handleIsOnChange}
      {...props}
    >
      {getSwitchChildren()}
    </div>
  );
}

export default SwitchButton;
